import { memo, useCallback } from "react";
import { MapPin, Heart } from "lucide-react";
import { formatCurrency } from "@/config/locale";
import { getAdImage } from "@/app/utils/ad-utils";
import { AdImage } from "@/app/components/common/ad-image";

export interface Ad {
  id: string;
  title: string;
  description: string;
  price: number;
  category: string;
  /** Catalog category_id (for filtering by category); set from feed/search when available. */
  category_id?: string;
  /** Full category path from feed e.g. "Instrumentos Musicais > Instrumentos de Corda". Used for display when present. */
  category_tree?: string;
  location: string;
  image?: string; // Main image (URL or legacy)
  imageUrl?: string; // Main image for backward compatibility
  images?: string[]; // Array of all images (URLs and/or data URLs)
  /** Opaque storage paths from upload flow (ads/ad_id/uuid.jpg). Resolved to signed URL for display. */
  user_images?: string[];
  createdAt?: string;
  seller?: string;
  /** Backend seller user id; used to hide cart/offer/chat when viewer is the seller. */
  seller_id?: string;
  /** Year the seller signed up (from BFF enrichment). Used for "Membro desde YEAR". */
  sellerMemberSinceYear?: number;
  condition?: string;
  brand?: string;
  acceptsOffers?: boolean; // Whether the seller accepts offers
  featured?: boolean; // Whether this is a featured/highlighted item
  /** Optional shipping attributes for freight quote (cm, kg). */
  shipping_weight_kg?: number;
  shipping_width_cm?: number;
  shipping_height_cm?: number;
  shipping_length_cm?: number;
  /** Seller CEP for shipping (swap: Leg 2.1 destination). */
  shipping_origin_cep?: string;
  /** Sambora listing status: active, draft, sold, archived (from API). */
  status?: string;
  /** BFF: false for review seed listings (robots noindex on web detail). */
  indexingAllowed?: boolean;
  /** Category-specific attributes from API (BFF `attributes`). */
  listing_attributes?: Record<string, unknown>;
}

interface AdCardProps {
  ad: Ad;
  isFavorite?: boolean;
  onToggleFavorite?: (id: string) => void;
  onAdClick?: () => void;
}

export const AdCard = memo(function AdCard({ ad, isFavorite, onToggleFavorite, onAdClick }: AdCardProps) {
  const handleFavoriteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleFavorite?.(ad.id);
  }, [ad.id, onToggleFavorite]);

  return (
    <div 
      onClick={onAdClick}
      className={`rounded-xl overflow-hidden shadow-sm border transition-all duration-300 cursor-pointer relative ${
        ad.featured
          ? "bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-orange-500/10 border-orange-400/60 shadow-lg shadow-orange-500/20 hover:shadow-xl hover:shadow-orange-500/30"
          : "bg-card border-border hover:shadow-md"
      }`}
    >
      {/* Featured Badge */}
      {ad.featured && (
        <div className="absolute top-3 left-3 z-10 px-3 py-1.5 bg-gradient-to-r from-blue-600 via-purple-600 to-orange-600 text-white text-xs font-bold rounded-full shadow-lg flex items-center gap-1.5 animate-pulse">
          <span className="w-2 h-2 bg-white rounded-full"></span>
          DESTAQUE
        </div>
      )}

      {/* Favorite Button */}
      {onToggleFavorite && (
        <button
          onClick={handleFavoriteClick}
          className="absolute top-3 right-3 z-10 w-10 h-10 bg-card/90 backdrop-blur-sm hover:bg-card rounded-full flex items-center justify-center shadow-md transition-all hover:scale-110"
          aria-label={isFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
        >
          <Heart
            className={`w-5 h-5 transition-colors ${
              isFavorite ? "fill-primary text-primary" : "text-muted-foreground"
            }`}
          />
        </button>
      )}
      
      <div className="aspect-[4/3] w-full overflow-hidden">
        <AdImage
          src={getAdImage(ad)}
          alt={ad.title}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
      </div>
      <div className="p-4">
        <h3 className="line-clamp-2 mb-2">{ad.title}</h3>
        <p className="text-primary font-semibold">{formatCurrency(ad.price)}</p>
        <div className="flex items-center gap-1 text-xs text-muted-foreground pt-3 mt-2 border-t border-border">
          <MapPin className="w-3 h-3" />
          <span>{ad.location}</span>
        </div>
      </div>
    </div>
  );
});